import type { Localite } from '~/api/gen'

/**
 * Types de localité issus d'une fusion de communes (`Localite.type`), affichés avec leur commune
 * de rattachement. Les codes postaux secondaires d'une commune et les quartiers portent aussi un
 * `parent` mais ne sont pas concernés.
 */
const TYPES_FUSIONNES = ['commune_ancienne', 'commune_deleguee']

export interface LocaliteItem {
  /** `codePostal nom` : format comparé à l'identique par le filtre de diffusion (`filterDiffusionRows`). */
  label: string
  /** Nom de la commune de rattachement d'une ancienne commune ou d'une commune déléguée. */
  parent?: string
}

/** Suggestion de localité affichée dans les `UInputMenu` des sidebars. */
export function toLocaliteItem(localite: Localite): LocaliteItem {
  const label = `${localite.codePostal} ${localite.nom}`
  const parent = localite.parent?.nom
  const fusionnee = localite.type !== undefined && TYPES_FUSIONNES.includes(localite.type)
  // Une commune déléguée peut porter le nom de la commune nouvelle (Annecy → Annecy) : rien à ajouter.
  if (fusionnee && parent && parent !== localite.nom)
    return { label, parent }
  return { label }
}

/**
 * Suggestions dédoublonnées par libellé : le datalake renvoie parfois deux lignes de même
 * `codePostal nom` (« 74000 Annecy » : commune et commune déléguée), or `value-key="label"`
 * impose un libellé unique par option. La première occurrence gagne (tri population décroissante).
 */
export function toLocaliteItems(localites: Localite[]): LocaliteItem[] {
  const seen = new Set<string>()
  return localites.map(toLocaliteItem).filter((item) => {
    if (seen.has(item.label))
      return false
    seen.add(item.label)
    return true
  })
}

/**
 * Props du `UInputMenu` des localités : menu aligné à gauche du champ et élargi à droite
 * jusqu'au nom le plus long (le thème fixe sinon sa largeur à celle du champ).
 */
export const LOCALITE_MENU_PROPS = {
  content: { align: 'start' },
  ui: { content: 'w-auto min-w-(--reka-combobox-trigger-width) max-w-[calc(100vw-1rem)]' },
} as const
